import _ from 'lodash'
import I18n from '@/services/i18n/i18n'
import { defaultPipeline, extractErrorWithPipeline } from './error'
const t = I18n.getInstance().getFixedNamespaceT('messages')

let toasts = []

function Toast (Vue) {
  const HIDE_DELAY = 2500

  const ToastBase = {
    props: {
      title: String,
      content: {
        type: String,
        required: true
      },
      autoHide: {
        type: [Boolean, Number],
        default: HIDE_DELAY
      },
      level: {
        type: String,
        defualt: 'info'
      }
    },
    mounted () {
      this.$nextTick(() => {
        this.visible = true
        if (this.autoHide) {
          const delay = typeof this.autoHide === 'boolean' ? HIDE_DELAY : this.autoHide
          _.delay(() => {
            this.visible = false
          }, delay)
        }
      })
    },
    computed: {
      levelClass () {
        return this.level
      }
    },
    methods: {
      close () {
        this.visible = false
      }
    }
  }

  const TopToastContructor = Vue.extend({
    ...ToastBase,
    template: `
      <transition name="slide-down" @after-leave="$emit('leave-transition-ended')">
        <div class="toast toast-top" :class="levelClass" v-show="visible">
          <div class="global-width-tiny">
            <slot>
              <p class="margin-0 text-center" v-html="content">
              </p>
            </slot>
          </div>
        </div>
      </transition>
    `
  })

  const TopCornerToastContructor = Vue.extend({
    ...ToastBase,
    template: `
      <transition name="slide-left" @after-leave="$emit('leave-transition-ended')">
        <div class="toast toast-general toast-plain" :class="levelClass" v-show="visible">
          <div class="toast-content">
            <h4 class="toast-heading" v-if="title">{{ title }}</h4>
            <span>{{ content }}</span>
          </div>
        </div>
      </transition>
    `
  })

  const BottomCornerToastContructor = Vue.extend({
    ...ToastBase,
    template: `
      <transition name="slide-left" @after-leave="$emit('leave-transition-ended')">
        <div class="toast toast-general toast-form" :class="levelClass" v-show="visible">
          <div class="toast-content">
            <h4 class="toast-heading">{{ title }}</h4>
            <slot>
              <div v-html="content">
              </div>
            </slot>
          </div>
        </div>
      </transition>
    `
  })

  const ToastTypes = {
    'top': TopToastContructor,
    'top-corner': TopCornerToastContructor,
    'bottom-corner': BottomCornerToastContructor
  }

  const ToastLevels = ['info', 'error', 'warning']

  const defaultOptions = {
    autoHide: HIDE_DELAY,
    hideOthers: true,
    level: 'info',
    type: 'top'
  }

  function initInstance (props, options) {
    const ToastContructor = ToastTypes[options.type]
    return new ToastContructor({
      data: {
        visible: false
      },
      el: document.createElement('template'),
      propsData: {
        ...props,
        autoHide: options.autoHide,
        level: options.level
      }
    })
  }

  function validateOptions (options) {
    if (ToastTypes[options.type] == null) {
      console.debug(`Toast type ${options.type} does not exist`)
    }
    if (ToastLevels.indexOf(options.level) < 0) {
      console.debug(`Toast level '${options.level}' is incorrect, should be ${ToastLevels}`)
    }
  }

  function show (props, options) {
    options = Object.assign({}, defaultOptions, options)
    props = typeof props === 'string' ? { content: props } : props

    validateOptions(options)

    if (options.hideOthers) {
      hideAll()
    }

    return new Promise(function (resolve) {
      const instance = initInstance(props, options)
      const insertedEl = instance.$el
      if (!instance) {
        throw Error(`Failed to init toast`)
      }
      document.body.appendChild(instance.$el)
      instance.$nextTick(() => {
        toasts.push(instance)
        instance.$on('leave-transition-ended', _ => {
          instance.$nextTick(_ => {
            document.body.removeChild(insertedEl)
            instance.$destroy()
          })
        })
        instance.$nextTick(_ => {
          resolve(instance)
        })
      })
    })
  }

  function hideAll () {
    toasts.forEach(toast => {
      toast.close()
    })
    toasts = []
  }

  function hideTopmost () {
    const topmost = toasts.pop()
    if (topmost) {
      topmost.close()
    }
  }

  function hide (toast) {
    if (toast == null) {
      return
    }
    const idx = _.findIndex(toasts, { _uid: toast._uid })
    if (idx > -1) {
      toasts[idx].close()
      toasts.splice(idx, 1)
    }
  }

  function showMessage (content, options = {}) {
    show(content, {
      autoHide: 2000,
      type: 'top',
      ...options,
      level: 'info'
    })
  }

  function showInfo ({ title, content }, options = {}) {
    show({ title, content }, {
      type: 'top-corner',
      autoHide: 2000,
      ...options,
      level: 'info'
    })
  }

  function showError (error, options = {}, pipeline = defaultPipeline) {
    const message = extractErrorWithPipeline(error, pipeline)

    if (message) {
      showErrorMessage(message, options)
    }
  }

  function showErrorMessage (content, options = {}) {
    show(content || t('general.errorTitle'), {
      autoHide: 3000,
      type: 'top',
      ...options,
      level: 'error'
    })
  }

  return {
    show,
    showMessage,
    showInfo,
    showError,
    showErrorMessage,
    hide,
    hideAll,
    hideTopmost
  }
}

function install (Vue) {
  Vue.prototype.$toast = Toast(Vue)
}

export default {
  install
}




// WEBPACK FOOTER //
// ./src/services/toast/index.js